import BlogHeader from '@/components/_blog-updates/BlogHeader';
import { isValidTheme } from '@/components/_blog-updates/BlogHeader/BlogHeader.utils';
import BlogSidebarRenderer from '@/components/_blog-updates/craft/sidebar/BlogSidebarRenderer';
import { PostThumbProps } from '@/components/_blog-updates/PostThumb';
import SidebarTableOfContents from '@/components/_blog-updates/SidebarTableOfContents';
import BlogFooter from '@/components/ArticleFooter';
import { Container } from '@/components/layout/Container';
import VizyBlogArticle from '@/components/vizy/VizyBlogArticle';
import { extractTocItems } from '@/components/vizy/VizyBlogArticle/VizyBlogArticle.utils';
import { GlobalContext } from '@/context';
import { BlogArticlePathsQuery, BlogArticleQuery, BlogArticleQueryVariables } from '@/graphql';
import BlogArticleQueryDocument from '@/graphql/queries/BlogArticle.gql';
import BlogArticlePathsQueryDocument from '@/graphql/queries/BlogArticlePaths.gql';
import { getPageProps } from '@/lib/craft-page';
import { createLogger } from '@/lib/debug';
import { fetchRelatedLinks } from '@/lib/graphite/fetch-related-links';
import { mapGraphiteApiResponseToLinkCardProps } from '@/lib/graphite/utilities';
import CraftClient from '@/lib/graphql';
import useBreakpoint from '@/lib/hooks/useBreakpoint';
import { getStaticPagePaths, getStaticPageProps } from '@/lib/next';
import { COLORS } from '@/styles/color';
import { getBrandColor } from '@/styles/color/utils';
import { marginX, marginY, mediaBreakpointDown, mediaBreakpointUp, paddingX, paddingY } from '@/styles/layout/utils';
import { FONT_WEIGHTS } from '@/styles/typography';
import { customTextSize, remCalc } from '@/styles/typography/utils';
import BlogTemplate from '@/templates/BlogTemplate';
import dayjs from 'dayjs';
import { useContext, useMemo, useRef } from 'react';
import styled from 'styled-components';

// #region - Components

interface ArticleWrapperProps {
  brandColor: string;
}


const ArticleWrapper = styled.div<ArticleWrapperProps>`
  --article-accent-color-base: ${(props) => getBrandColor(props.brandColor ?? 'blue', 'base')};
  --article-accent-color-highlight: ${(props) => getBrandColor(props.brandColor ?? 'blue', 'highlight')};
  --article-accent-color-tint: ${(props) => getBrandColor(props.brandColor ?? 'blue', 'tint')};
`;

const Hero = styled.header`
  ${paddingY(144, 32)}
  background-color: var(--article-accent-color-tint);
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${remCalc(24)};
`;

const BackLink = styled.a`
  ${customTextSize(13, 21)};
  font-weight: ${FONT_WEIGHTS.BOLD};
  display: inline-block;
  border-bottom: 2px solid;
  ${paddingX(2)}

  ${mediaBreakpointUp('desktop')} {
    ${customTextSize(17, 27)};
  }
`;

const ArticleTitle = styled.h1`
  ${customTextSize(38, 42)};
  font-weight: ${FONT_WEIGHTS.MEDIUM};

  ${mediaBreakpointUp('desktop')} {
    ${customTextSize(80, 88)};
  }
`;

const CoverImageWrapper = styled.div`
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 60%;
    background-color: var(--article-accent-color-tint);
    z-index: -1;
  }
`;

const CoverImage = styled.div`
  position: relative;
  border-radius: ${remCalc(10)};
  width: 100%;
  overflow: hidden;

  img {
    width: 100%;
  }
`;

const AuthorBlock = styled.div`
  ${marginY(24)}
  display: flex;
  flex-direction: column;

  ${mediaBreakpointUp('desktop')} {
    flex-direction: row;
    align-items: center;
    grid-row: 2;
    column-gap: ${remCalc(20)};
    ${marginY(0)};
  }
`;

const AuthorPhoto = styled.div`
  position: relative;
  width: ${remCalc(57)};
  height: ${remCalc(57)};
  border-radius: 100%;
  background-color: var(--article-accent-color-highlight);
  overflow: hidden;
  margin-bottom: ${remCalc(12)};

  img {
    object-fit: cover;
    object-position: center;
  }

  ${mediaBreakpointUp('desktop')} {
    margin-bottom: 0;
  }
`;

const AuthorName = styled.p`
  text-transform: uppercase;
  ${customTextSize(15, 21)};
  letter-spacing: 0.04em;
  font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  margin-bottom: ${remCalc(2)};
`;

const AuthorTitle = styled.p`
  ${customTextSize(12, 18)};
  margin-bottom: ${remCalc(2)};
`;

const ArticleMetadata = styled.p`
  ${customTextSize(12, 18)};
  color: ${COLORS.GRAY.BASE};
`;

const ArticleSummary = styled.p`
  ${customTextSize(22, 30)};
  max-width: ${remCalc(960)};
  margin-bottom: ${remCalc(36)};

  ${mediaBreakpointUp('desktop')} {
    ${customTextSize(26, 37)};
    font-weight: ${FONT_WEIGHTS.MEDIUM};
    grid-row: 1;
    grid-column: 1 / -1;
    margin-top: 0;
    margin-bottom: ${remCalc(36)};
  }
`;

const Article = styled.article`
  max-width: ${remCalc(960)};
  ${marginX('auto')};
  width: 100%;

  ${mediaBreakpointDown('tablet')} {
    padding-bottom: ${remCalc(60)};
  }
`;

const ArticleAuthorRow = styled.div`
  grid-column: 1 / -1;

  & > div {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    max-width: ${remCalc(960)};
    margin: 0 auto;
  }
`;

interface ArticleGridProps {
  hasSummary: boolean;
}

const ArticleGrid = styled.div<ArticleGridProps>`
  display: grid;
  grid-template-columns: 100%;
  margin-bottom: ${remCalc(60)};

  ${mediaBreakpointDown('tablet')} {
    margin-top: ${remCalc(36)};
  }

  ${mediaBreakpointUp('desktop')} {
    grid-template-columns: minmax(${remCalc(280)}, ${remCalc(370)}) minmax(${remCalc(500)}, ${remCalc(700)});
    grid-template-rows: repeat(${(props) => (props.hasSummary ? 3 : 2)}, auto);
    column-gap: ${remCalc(40)};
    justify-content: space-between;
    row-gap: ${remCalc(40)};

    ${Article} {
      grid-row: ${(props) => (props.hasSummary ? 3 : 2)};
      grid-column: 2;
    }
  }
`;

interface SidebarProps {
  hasSummary: boolean;
}

const Sidebar = styled.aside<SidebarProps>`
  display: none;

  ${mediaBreakpointUp('desktop')} {
    display: flex;
    flex-direction: column;
    gap: ${remCalc(32)};
    grid-column: 1;
    grid-row: ${(props) => (props.hasSummary ? 3 : 2)};
    position: sticky;
    top: ${remCalc(100)};
    align-self: start;
  }
`;

// #endregion - Components


interface PageProps extends BlogArticleQuery {
  relatedArticles?: PostThumbProps[];
}

const BlogArticle: PageTemplate<PageProps> = ({ entry, relatedArticles }) => {

  const { previousRouterPath } = useContext(GlobalContext);
  const articleRef = useRef<HTMLElement>(null);
  const [, activeBreakpoints] = useBreakpoint();

  /* Calculate estimated read time based on 225 words per minute */
  const readTime = useMemo(
    () => Math.ceil((articleRef.current?.textContent?.split(' ').length || 0) / 225),
    [articleRef.current],
  );

  const coverImage = entry?.coverImage?.[0];

  const author = entry?.contentAuthor?.[0];

  const blogCategories = entry?.blogCategories?.map((category) => ({
    label: category?.title ?? '',
    href: category?.slug ? `/blog/category/${category?.slug}` : '#',
  }))

  const tocItems = useMemo(
    () => extractTocItems(entry?.blogArticleContent?.rawNodes),
    [entry?.blogArticleContent?.rawNodes],
  );

  const hasSidebarModules = !!(entry?.blogSidebarModules?.length);
  const hasToc = tocItems.length > 0;

  const blogHomeCrumb = [{ label: 'Blog', href: '/blog' }];

  const firstBlogCategory = blogCategories?.[0];

  /* Use the first blog category for the breadcrumbs */
  const blogBreadcrumbs = firstBlogCategory ? [...blogHomeCrumb, { label: firstBlogCategory?.label ?? '', href: firstBlogCategory?.href ?? '#' }] : blogHomeCrumb;

  const readTimeText = `${readTime} min read`;

  const postDate = dayjs(entry?.postDate).format(`D MMMM YYYY`);

  const emailBody = `Hey there,\n\nI enjoyed this article from Front Page and thought you might like it too. Let me know what you think!\n\n${entry?.url}`;

  const pageUrl = entry?.url ?? '';


  return (
    <>
      <ArticleWrapper brandColor={entry?.brandColor?.label!}>

        <BlogHeader
          theme={isValidTheme(entry?.blogHeaderTheme) ? entry?.blogHeaderTheme : 'violet'}
          breadcrumbs={blogBreadcrumbs}
          title={entry?.title ?? ''}
          author={{
            name: author?.authorName ?? '',
            role: author?.authorTitle ?? '',
            imageSrc: author?.authorPhoto?.[0]?.url ?? '',
            imageAlt: author?.authorName ?? '',
          }}
          date={postDate}
          readTime={readTimeText}
          heroImage={coverImage?.url ?? ''}
          heroImageSize={(entry?.heroImageSize as 'large' | 'small') || 'small'}
          className='mt-12'
          pageUrl={pageUrl}
          emailBody={emailBody}
        />

        <Container>
          <ArticleGrid hasSummary={!!entry?.summary}>


            <Article ref={articleRef}>
              {entry?.summary && <ArticleSummary>{entry?.summary}</ArticleSummary>}
              <VizyBlogArticle nodes={entry?.blogArticleContent?.nodes} rawNodes={entry?.blogArticleContent?.rawNodes} />

            </Article>

            <Sidebar hasSummary={!!entry?.summary}>
              {hasToc && (
                <SidebarTableOfContents title="In this article" items={tocItems} />
              )}
              {hasSidebarModules && (
                <BlogSidebarRenderer blocks={entry?.blogSidebarModules ?? []} />
              )}
            </Sidebar>
          </ArticleGrid>
        </Container>
      </ArticleWrapper>
      {relatedArticles && relatedArticles.length > 0 && (
        <BlogFooter carouselTitle="Related articles"
          theme='dark'
          relatedArticles={relatedArticles}
        />
      )}
    </>
  );
};

BlogArticle.template = BlogTemplate;

export default BlogArticle;

export const getStaticPaths = getStaticPagePaths('blog', async (context) => {

  try {
    const client = new CraftClient();

    const data = await client.request<BlogArticlePathsQuery>(BlogArticlePathsQueryDocument);

    const paths =
      data.entries?.map((path) => ({
        params: {
          slug: path?.slug!,
        },
      })) || [];

    return {
      paths,
      fallback: 'blocking',
    };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error fetching blog paths from GraphQL:', error);

    return {
      paths: [],
      fallback: 'blocking',
    };
  }
});


export const getStaticProps = getStaticPageProps<PageProps, { slug: string }>(
  (params) => `blog/${params?.slug}`,
  async ({ client, context: { params } }) => {
    const slug = params?.slug!;

    const logger = createLogger('pages/blog/[slug].tsx');

    logger.info(`Building blog article: ${slug}`);

    try {

      const pageProps = await getPageProps<BlogArticleQuery, BlogArticleQueryVariables>({
        pathPrefix: '/blog',
        queryDocument: BlogArticleQueryDocument,
        client,
        variables: {
          slug,
        },
      });

      if (!pageProps) return { notFound: true };

      // Fetch related links for the article
      const graphiteRelatedLinks = await fetchRelatedLinks({ pathPrefix: 'blog', slug });
      const relatedArticles = mapGraphiteApiResponseToLinkCardProps(graphiteRelatedLinks);

      return { props: { ...pageProps, relatedArticles } };
    } catch (error) {
      logger.error(`Error building blog article ${slug}:`, error);

      /*
       * In preview environments, if there's an error, return notFound
       * This prevents the build from failing completely
       */
      return { notFound: true };
    }
  },
);
